import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Contrato, ContratoParcela } from '@services/db.js'
import {
  addContrato,
  addParcela,
  deleteContrato,
  deleteParcela,
  desfazerLiberacao,
  desfazerPagamento,
  getContrato,
  getContratos,
  getParcelas,
  liberarParcela,
  pagarParcela,
  resumirContrato,
  updateContrato,
  updateParcela,
  type ContratoResumo,
  type NovaParcela,
  type NovoContrato,
  type MedicaoInput,
  type PagamentoParcelaInput,
} from '@services/gestao.js'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'

const FASE_LABEL: Record<string, string> = Object.fromEntries(
  FASES_CONSTRUTIVAS.map((f) => [f.id, f.label]),
)

export interface ContratoItem extends Contrato {
  id: number
  parcelas: ContratoParcela[]
  resumo: ContratoResumo
  faseLabel?: string
}

export interface ContratosTotais {
  contratado: number
  pago: number
  aPagar: number
  ativos: number
  vencidas: number
}

function enriquecer(c: Contrato, parcelas: ContratoParcela[]): ContratoItem {
  const faseLabel = c.faseConstrutiva ? FASE_LABEL[c.faseConstrutiva] : undefined
  return {
    ...c,
    id: c.id as number,
    parcelas,
    resumo: resumirContrato(c, parcelas),
    ...(faseLabel ? { faseLabel } : {}),
  }
}

export function useContratos() {
  const [itens, setItens] = useState<ContratoItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const [contratos, parcelas] = await Promise.all([getContratos(), getParcelas()])
    const porContrato = new Map<number, ContratoParcela[]>()
    for (const p of parcelas) {
      const list = porContrato.get(p.contratoId) ?? []
      list.push(p)
      porContrato.set(p.contratoId, list)
    }
    setItens(contratos.map((c) => enriquecer(c, porContrato.get(c.id as number) ?? [])))
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const totais = useMemo<ContratosTotais>(() => {
    const validos = itens.filter((c) => c.status !== 'cancelado')
    return {
      contratado: validos.reduce((s, c) => s + c.valorTotal, 0),
      pago: validos.reduce((s, c) => s + c.resumo.pago, 0),
      aPagar: validos.reduce((s, c) => s + c.resumo.aPagar, 0),
      ativos: validos.filter((c) => c.status === 'ativo').length,
      vencidas: validos.reduce((s, c) => s + c.resumo.parcelasVencidas, 0),
    }
  }, [itens])

  const criar = useCallback(
    async (input: NovoContrato, parcelas: NovaParcela[]) => {
      const id = await addContrato(input, parcelas)
      await load()
      return id
    },
    [load],
  )

  const remover = useCallback(async (id: number) => {
    await deleteContrato(id)
    setItens((prev) => prev.filter((c) => c.id !== id))
  }, [])

  return { itens, totais, isLoading, criar, remover, reload: load }
}

export function useContrato(id: number | null) {
  const [item, setItem] = useState<ContratoItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const load = useCallback(async () => {
    if (id == null || Number.isNaN(id)) {
      setNotFound(true)
      setIsLoading(false)
      return
    }
    const [c, parcelas] = await Promise.all([getContrato(id), getParcelas(id)])
    if (!c) {
      setNotFound(true)
      setItem(null)
    } else {
      setItem(enriquecer(c, parcelas))
      setNotFound(false)
    }
    setIsLoading(false)
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const wrap =
    <A extends unknown[]>(fn: (...args: A) => Promise<unknown>) =>
    async (...args: A) => {
      await fn(...args)
      await load()
    }

  return {
    item,
    isLoading,
    notFound,
    reload: load,
    editar: wrap((patch: Partial<NovoContrato>) => updateContrato(id!, patch)),
    adicionarParcela: wrap((data: NovaParcela) => addParcela(id!, data)),
    editarParcela: wrap(
      (
        parcelaId: number,
        patch: Partial<Omit<ContratoParcela, 'id' | 'contratoId' | 'criadoEm'>>,
      ) => updateParcela(parcelaId, patch),
    ),
    removerParcela: wrap((parcelaId: number) => deleteParcela(parcelaId)),
    liberar: wrap((parcelaId: number, medicao?: MedicaoInput) =>
      liberarParcela(parcelaId, medicao),
    ),
    desfazerLiberar: wrap((parcelaId: number) => desfazerLiberacao(parcelaId)),
    pagar: wrap((parcelaId: number, input: PagamentoParcelaInput) =>
      pagarParcela(parcelaId, input),
    ),
    desfazerPagar: wrap((parcelaId: number) => desfazerPagamento(parcelaId)),
  }
}
